import Icon from '@/shared/assets/icons/orderIcon.svg?react'
import { BaseButton } from '@/shared/components'
import { OrderIcon } from '@/shared/components/icons/OrderIcon'
import { ItemLayout } from '@/shared/components/ItemLayout/ItemLayout'
import { formatDate, formatTimeFromSeconds } from '@/shared/lib/date'
import { ColumnDef } from '@tanstack/react-table'
import { DisputStatusIcons, DisputStatusTitles } from '@/features/orders'
import { DisputListModel, DisputStatus } from '../../model/types'
import { formatCardOrPhone } from '@/features/orders'
import { getDisputAmount } from '@/features/orders'
import { Roles } from '@/entities/roles'
import { formatCurrency } from '@/shared/lib/currency'
import { Currencies } from '@/entities/curency'
import { getShortenId } from '@/shared/utils/getShortenId'
import { Link } from 'react-router-dom'
import { CopyBlock } from '@/shared/components/CopyBlock/CopyBlock'

type Props = {
  openDisputDialog?: (id: string) => void
  inProgressDisputDialog?: (id: string) => void
  userRole: Roles | null
}

export const getDisputColumns = ({
  openDisputDialog,
  inProgressDisputDialog,
  userRole,
}: Props): ColumnDef<DisputListModel>[] => {
  const isMerchant = userRole === Roles.Merchant
  const isTrader = userRole === Roles.Trader

  const baseColumns: ColumnDef<DisputListModel>[] = [
    {
      accessorKey: 'id',
      header: () => <div className="uppercase">id</div>,
      cell: ({ row }) => {
        const id = row.original.id
        return (
          <CopyBlock text={id} title={id}>
            <Link to={id}>{getShortenId(id)}</Link>
          </CopyBlock>
        )
      },
    },
    {
      accessorKey: 'order',
      header: 'Спор',
      cell: ({ row }) => {
        const disput = row.original
        const id = row.original.id
        return (
          <Link to={id}>
            <ItemLayout
              icon={<OrderIcon status={DisputStatusIcons[disput.status]} />}
              subtitle={formatDate(disput.createdAt)}
              title={DisputStatusTitles[disput.status]}
              subtitleClasses="whitespace-nowrap"
            />
          </Link>
        )
      },
    },
    {
      id: 'dealAmount',
      accessorKey: 'dealAmount',
      header: 'Сумма сделки',
      cell: ({ row }) => {
        const { disputAmountRub, disputAmountUSDT } = getDisputAmount(row.original)

        return (
          <div className="flex flex-col">
            <CopyBlock text={disputAmountRub?.toFixed(2)}>
              {formatCurrency(disputAmountRub, Currencies.RUB)}
            </CopyBlock>
            <CopyBlock text={disputAmountUSDT?.toFixed(2)} color="secondary">
              {formatCurrency(disputAmountUSDT, Currencies.USDT)}
            </CopyBlock>
          </div>
        )
      },
    },
    {
      id: 'disputAmount',
      accessorKey: 'disputAmount',
      header: 'Сумма спора',
      cell: ({ row }) => {
        return (
          <div className="flex flex-col">
            <CopyBlock text={row.original.disputAmountFromMerchant?.toFixed(2)}>
              {formatCurrency(row.original.disputAmountFromMerchant, Currencies.RUB)}
            </CopyBlock>
            <CopyBlock
              text={row.original.disputAmountFromMerchantUsdt?.toFixed(2)}
              color="secondary"
            >
              {formatCurrency(row.original.disputAmountFromMerchantUsdt, Currencies.USDT)}
            </CopyBlock>
          </div>
        )
      },
    },
    {
      accessorKey: 'requisite',
      header: 'Реквизит',
      cell: ({ row }) => {
        return (
          <div className="flex flex-col">
            <CopyBlock text={row.original.cardNumber}>
              {formatCardOrPhone(row.original.cardNumber ?? '')}
            </CopyBlock>
            <span className="text-text-secondary">{row.original.bankName || ''}</span>
          </div>
        )
      },
    },
  ]

  const merchantOnlyColumn: ColumnDef<DisputListModel> = {
    accessorKey: 'time',
    header: 'Время',
    cell: ({ row }) => {
      const showTime =
        row.original.status === DisputStatus.Created ||
        row.original.status === DisputStatus.InProgress

      if (!showTime) return <span></span>
      const secondsDiff = Math.floor(
        (Date.now() - new Date(row.original.createdAt).getTime()) / 1000
      )
      const formattedTime = formatTimeFromSeconds(secondsDiff)
      return <span className="text-primary dark:text-primary-dark">{formattedTime}</span>
    },
  }

  const traderOnlyColumns: ColumnDef<DisputListModel>[] = [
    {
      accessorKey: 'device',
      header: 'Устройство',
      cell: ({ row }) => {
        return (
          <div className="flex flex-col">
            <span>{row.original.deviceName}</span>
            <span className="text-text-secondary">{row.original.deviceId}</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'action',
      header: '',
      cell: ({ row }) => {
        const status = row.original.status
        if (status === DisputStatus.Created || status === DisputStatus.Reopened) {
          return (
            <BaseButton
              onClick={(event) => {
                event.stopPropagation()
                event.preventDefault()
                openDisputDialog?.(row.original.id)
              }}
              radius="lg"
              icon={<Icon />}
              className="w-[200px] whitespace-nowrap"
            >
              Взять в работу
            </BaseButton>
          )
        } else if (status === DisputStatus.InProgress) {
          return (
            <BaseButton
              onClick={(event) => {
                event.stopPropagation()
                event.preventDefault()
                inProgressDisputDialog?.(row.original.id)
              }}
              radius="lg"
              icon={<Icon />}
              className="w-[200px] whitespace-nowrap"
              variant="outlined"
            >
              Перейти к спору
            </BaseButton>
          )
        }
      },
    },
  ]

  return [
    ...baseColumns,
    ...(isMerchant ? [merchantOnlyColumn] : []),
    ...(isTrader ? traderOnlyColumns : []),
  ]
}
